import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, ArrowRight, ArrowLeft, Sparkles, RefreshCw, ShoppingBag } from 'lucide-react';
import api from '@/lib/api';
import { cx } from '@/lib/format';
import ProductCard from '@/components/common/ProductCard';
import Loader from '@/components/common/Loader';

export default function GiftFinder() {
  const [questions, setQuestions] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [matched, setMatched] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/gift-finder/questions').then((r) => setQuestions(r.data || [])).catch(() => setQuestions([]));
  }, []);

  const labelFor = useMemo(() => {
    const m = {};
    (questions || []).forEach((q) => (q.options || []).forEach((o) => { m[`${q.key}:${o.value}`] = o.label; }));
    return m;
  }, [questions]);

  const finish = async (finalAnswers) => {
    setLoading(true); setResults([]);
    try {
      const { data } = await api.post('/gift-finder/match', { answers: finalAnswers, limit: 12 });
      setResults(data.results || []); setMatched(data.matched || 0);
    } catch { setResults([]); } finally { setLoading(false); }
  };

  const reset = () => { setStep(0); setAnswers({}); setResults(null); };

  if (questions === null) return <div className="container-gaze py-16"><Loader label="Loading Gift Finder" /></div>;

  if (!questions.length && !results) {
    return (
      <div className="container-gaze py-16 text-center">
        <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-[hsl(var(--gaze-ivory))]"><Gift size={26} className="gaze-gold" /></div>
        <h1 className="font-display text-3xl">Gift Finder</h1>
        <p className="mt-2 text-sm text-muted-foreground">The Gift Finder is being curated. Meanwhile, explore our full collection.</p>
        <Link to="/shop" data-testid="gift-finder-shop-cta" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-white"><ShoppingBag size={15} /> Shop All Decor</Link>
      </div>
    );
  }

  if (results) {
    const chips = Object.entries(answers).filter(([, v]) => v).map(([k, v]) => labelFor[`${k}:${v}`] || v);
    return (
      <div className="container-gaze py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow mb-2 flex items-center gap-1"><Sparkles size={13} className="gaze-gold" /> Your Curated Gifts</p>
            <h1 className="text-3xl font-semibold">Perfect picks, just for them</h1>
            {!!chips.length && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="gift-finder-answer-chips">
                {chips.map((c) => <span key={c} className="rounded-full bg-[hsl(var(--gaze-ivory))] px-3 py-1 text-xs font-medium text-[hsl(var(--primary))]">{c}</span>)}
              </div>
            )}
            {!loading && matched > 0 && <p className="mt-2 text-xs text-muted-foreground">{matched} matched your answers{results.length > matched ? ` · ${results.length - matched} popular picks added` : ''}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={reset} data-testid="gift-finder-restart" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold"><RefreshCw size={15} /> Start over</button>
            <Link to="/shop" data-testid="gift-finder-shop-cta" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-5 py-2 text-sm font-semibold text-white"><ShoppingBag size={15} /> Shop All Decor</Link>
          </div>
        </div>
        {loading ? <Loader label="Finding perfect gifts" /> : results.length ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {results.map((p, i) => <div key={p.id} data-testid="gift-finder-result-card"><ProductCard product={p} index={i} /></div>)}
          </div>
        ) : (
          <div className="rounded-3xl border border-border bg-white p-10 text-center">
            <p className="text-sm text-muted-foreground">We couldn't find a match for those answers — explore our full collection instead.</p>
            <Link to="/shop" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-white"><ShoppingBag size={15} /> Shop All Decor</Link>
          </div>
        )}
      </div>
    );
  }

  const cur = questions[step];
  const selected = answers[cur.key];
  const isLast = step === questions.length - 1;

  const advance = (a) => { if (isLast) finish(a); else setStep((s) => s + 1); };
  const skip = () => { const a = { ...answers }; delete a[cur.key]; setAnswers(a); advance(a); };

  return (
    <div className="container-gaze py-12">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-[hsl(var(--gaze-ivory))]"><Gift size={26} className="gaze-gold" /></div>
          <h1 className="font-display text-3xl">Gift Finder</h1>
          <p className="mt-1 text-sm text-muted-foreground">Answer a few questions and we'll curate the perfect gift.</p>
        </div>
        <div className="mb-6 flex gap-2">{questions.map((s, i) => <div key={s.key} className={cx('h-1.5 flex-1 rounded-full', i <= step ? 'bg-[hsl(var(--gaze-gold))]' : 'bg-border')} />)}</div>
        <div className="rounded-3xl border border-border bg-white p-6 sm:p-8">
          <p className="eyebrow mb-1">Step {step + 1} of {questions.length}</p>
          <h2 className="mb-1 text-2xl font-semibold">{cur.title}</h2>
          {cur.subtitle && <p className="mb-4 text-sm text-muted-foreground">{cur.subtitle}</p>}
          <div className={cx('grid grid-cols-2 gap-3', !cur.subtitle && 'mt-5', 'sm:grid-cols-3')}>
            {cur.options.map((o) => {
              const active = selected === o.value;
              return (
                <button key={o.value} onClick={() => setAnswers((a) => ({ ...a, [cur.key]: o.value }))} data-testid={`gift-finder-option-${o.value}`} className={cx('rounded-2xl border p-4 text-sm font-medium transition', active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--gaze-ivory))]' : 'border-border hover:hairline-gold')}>
                  {o.label}
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground disabled:opacity-40"><ArrowLeft size={15} /> Back</button>
            <div className="flex items-center gap-4">
              <button onClick={skip} data-testid="gift-finder-skip-button" className="text-xs font-semibold text-muted-foreground underline-offset-2 hover:underline">Skip</button>
              {!isLast ? (
                <button onClick={() => advance(answers)} disabled={!selected} data-testid="gift-finder-next-button" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-50">Next <ArrowRight size={15} /></button>
              ) : (
                <button onClick={() => finish(answers)} disabled={!selected} data-testid="gift-finder-finish-button" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--gaze-gold))] px-6 py-3 text-sm font-semibold text-black disabled:opacity-50">Show Gifts <Sparkles size={15} /></button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
